import React, {useEffect, useState} from 'react'
import {
  Townhall,
  useTownhallTemplate,
  useTownhallUpdate,
} from 'Event/template/Townhall'
import Settings from 'Event/template/Townhall/Dashboard/NavBar/NavBarConfig/Settings'
import Styling from 'Event/template/Townhall/Dashboard/NavBar/NavBarConfig/Styling'
import SettingsPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/SettingsPanel'
import StylingPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/StylingPanel'
import Config from 'organization/Event/Configurable/Config'
import {Emoji} from 'Event/Dashboard/components/EmojiList/emoji'
import {useAddTranslation} from 'Event/LanguageProvider'
import {replaceAtPath} from 'lib/JsonUpdateProvider'
import {useAutoUpdate} from 'organization/Event/TemplateEditor'
import {useConfig} from 'organization/Event/Configurable'
import {useDeleteFile} from 'Event/template/Townhall/Dashboard/Main/ResourceSection/ResourceUpload'

export function NavBarConfig() {
  const {emojiList} = useTownhallTemplate()

  const deleteFile = useDeleteFile()
  const update = useTownhallUpdate()
  const addTranslation = useAddTranslation()
  const {showing} = useConfig()

  const [emojis, setEmojis] = useState<Emoji['name'][]>(emojiList.emojis)
  const [customEmojis, setCustomEmojis] = useState<Emoji['name'][]>(
    emojiList.customEmojis ?? [],
  )

  useEffect(() => {
    if (showing) {
      return
    }

    setEmojis(emojiList.emojis)
    setCustomEmojis(emojiList.customEmojis ?? [])
  }, [showing, emojiList])

  const onSave = (data: any) => {
    const label = replaceAtPath(
      data,
      'navBar.mainMenuItemTitle',
      '{{navbar_main_menu_item_title}}',
    )

    const updated = {
      ...data,
      emojiList: {
        emojiWidth: data.emojiList.emojiWidth,
        emojis,
        customEmojis,
      },
      navBar: {
        ...data.navBar,
      },
      localization: addTranslation({
        navbar_main_menu_item_title: label ?? 'Main',
      }),
    }
    update(updated)
  }

  const addCustomEmoji = (emojiName: Emoji['name']) => {
    const changedCustomEmojies = [...customEmojis, emojiName]
    setCustomEmojis(changedCustomEmojies)
    setEmojis((curr) => [...curr, emojiName])

    const data: Townhall['emojiList'] = {
      ...emojiList,
      customEmojis: changedCustomEmojies,
    }

    update({
      emojiList: data,
    })
  }

  const removeCustomEmoji = (emojiName: Emoji['name']) => {
    // set current custom emojies
    const changedCustomEmojies = customEmojis.filter((e) => e !== emojiName)
    setCustomEmojis(changedCustomEmojies)

    // save custom emojies to API
    const data: Townhall['emojiList'] = {
      ...emojiList,
      customEmojis: changedCustomEmojies,
    }
    update({
      emojiList: data,
    })

    // Delete Custom emoji file
    deleteFile(emojiName)

    // Remove all emojies with that custom emoji
    const removed = emojis.filter((e) => e !== emojiName)
    setEmojis(removed)
  }

  useAutoUpdate({
    values: {
      emojiList: {
        emojis,
      },
    },
    when: showing,
  })

  return (
    <Config title="Townhall NavBar" onSave={onSave}>
      <SettingsPanel>
        <Settings
          emojis={emojis}
          setEmojis={setEmojis}
          customEmojis={customEmojis}
          addCustomEmoji={addCustomEmoji}
          removeCustomEmoji={removeCustomEmoji}
        />
      </SettingsPanel>
      <StylingPanel>
        <Styling />
      </StylingPanel>
    </Config>
  )
}
